import styled from '@emotion/styled'
import { useRouter } from 'next/router'
import { colors, fontFamilies } from '@configs/styles/config'
import ArrowDown from '@public/static/icons/referrals/arrowDown'
import Version from '@type/course/version'
import { getLocalizedCategoryName } from '@utils/getLocalizedCategoryName'

type Props = {
  course: Version | null
}

const MobileHeader = ({ course }: Props) => {
  const router = useRouter()

  const handleBack = () => {
    // Go back to wherever the user came from (preserving scroll/filters), but
    // fall back to the courses catalog when there's no in-app history — e.g. a
    // direct/shared link or a course opened in a new tab. Next.js (pages
    // router) increments history.state.idx per navigation, so idx > 0 means
    // there is a previous in-app entry to return to.
    const hasAppHistory =
      typeof window !== 'undefined' && (window.history.state?.idx ?? 0) > 0
    if (hasAppHistory) {
      router.back()
    } else {
      router.push('/courses')
    }
  }

  return (
    <Header>
      <Left className="overflow-x-auto scrollbar-hide">
        <button
          onClick={handleBack}
          className="min-w-[20px]"
          aria-label="Back"
        >
          <ArrowDown className="rotate-90" height={'18'} width={'16'} />
        </button>
        <SectionsRouteContainer
          hiddenOnMobile={false}
          className="scrollbar-hide"
        >
          <RouteButton
            onClick={() => {
              router.push({
                pathname: '/courses',
                query: {
                  category: JSON.stringify([course?.category?._id]),
                  subCategory: JSON.stringify([]),
                },
              })
            }}
          >
            {getLocalizedCategoryName(course?.category?.name, router.locale)}
          </RouteButton>
          <Separator>/</Separator>
          <RouteButton
            onClick={() => {
              router.push({
                pathname: '/courses',
                query: {
                  category: JSON.stringify([course?.category?._id]),
                  subCategory: JSON.stringify([course?.subCategory?._id]),
                },
              })
            }}
            className="!font-semiBold"
          >
            {course?.subCategory?.name?.en}
          </RouteButton>
        </SectionsRouteContainer>
      </Left>
      <Right></Right>
    </Header>
  )
}

export default MobileHeader

const Header = styled.div`
  justify-content: space-between;
  align-items: center;
  /* Left padding compensates the -0.4rem bleed so the back arrow aligns with
     the page content consistently across all mobile/tablet widths. */
  padding: 0px 1rem;
  padding-left: 0.9rem;
  height: 70px;
  border-bottom: 1px solid #eee;
  display: none;
  z-index: 20;
  background-color: ${colors.uguWhite};
  position: sticky;
  top: 0;
  @media (max-width: 390px) {
    width: 100%;
    margin-left: 0rem;
    max-width: 100%;
    padding-left: 1rem;
  }
  @media (max-width: 1050px) {
    display: flex;
  }
  width: calc(100% + 0.8rem);
  margin-left: -0.4rem;
  max-width: calc(100% + 0.8rem);
  overflow: auto;
`
const Left = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  max-width: 100%;
`
const Right = styled.div``
const SectionsRouteContainer = styled.div`
  display: flex;
  overflow: auto;
  align-items: center;
  gap: 0.4rem;
  @media (max-width: 1050px) {
    /* margin-top: 1.5rem; */
    display: ${(props) => (props.hiddenOnMobile ? 'none' : 'flex')};
  }
`

const RouteButton = styled.button`
  color: #1a1e3d;
  padding: 0;
  font-size: 1rem;
  background: none;
  transition: 0.2s;
  white-space: nowrap;
  line-height: 26px;
  font-weight: 300;
  letter-spacing: 0rem;
  font-family: ${fontFamilies.regular};
  @media (max-width: 640px) {
    font-size: 1rem;
  }
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
  :hover {
    font-weight: 500;
    cursor: pointer;
  }
`

const Separator = styled.div`
  font-size: 1rem;
  color: #1a1e3d;
`
